import { Bar, Link, SelectPlan, SelectTier } from "@/components";
import { Product, Plan, ProductId, Tier } from "@/types";
import { products } from "@/utils";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as LogoSvg } from "@/assets/svg/logo.svg";
import { doCheckout } from "@/apis";

export const SignUpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = (searchParams.get("product") || "white-glove") as ProductId;
  const product: Product = get(products, productId) as Product;
  const [plan, setPlan] = useState<Plan>();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(0);
  const [isLoading, setLoading] = useState(false);

  const onGoBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      window.history.back();
    }
  };

  const handleCheckout = async (tier: Tier) => {
    try {
      setLoading(true);

      if (isEmpty(tier)) return;

      const { url }: any = await doCheckout({
        email,
        line_items: [
          {
            price: tier.stripePriceId,
            quantity: 1,
          },
        ],
        mode: "subscription",
        success_url: window.location.origin + "/thank-you",
        metadata: tier.metadata,
      });

      window.open(url, "__self");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    const title =
      productId === ProductId.WhiteGlove ? (
        <>
          <span>White Glove</span>
          <span className="text-[#8DE5E9]">Concierge Service</span>
        </>
      ) : productId === ProductId.WhiteGlovePlus ? (
        <span>
          White Glove&nbsp;
          <span className="text-[#8DE5E9]">+ 409a</span>
        </span>
      ) : productId === ProductId.F409A ? (
        <span>
          409a&nbsp;
          <span className="text-[#8DE5E9]">Valuations</span>
        </span>
      ) : (
        <>
          <span>Unlimited</span>
          <span className="text-[#8DE5E9]">Cap Table Management</span>
        </>
      );

    if (step === 0)
      return (
        <SelectPlan
          title={title}
          product={product}
          plan={plan}
          onClickPlan={setPlan}
          onClickSubmit={({ email }) => {
            setEmail(email);
            setStep(step + 1);
          }}
        />
      );

    if (step === 1 && plan)
      return (
        <SelectTier
          loading={isLoading}
          title={title}
          plan={plan}
          onClickSubmit={handleCheckout}
        />
      );
  };

  return (
    <div className="flex h-screen bg-whitebg overflow-x-hidden overflow-y-auto">
      <div className="w-full xl:w-2/5 xl:min-w-[590px] p-6 xl:p-16 bg-dark inline-flex justify-center overflow-y-auto overflow-x-hidden">
        <div className="w-full xl:min-w-[480px] max-w-[590px] flex flex-col gap-10 h-fit">
          <div className="flex items-center justify-center relative">
            <button
              className="inline-flex items-center text-sm gap-2 text-white absolute left-0"
              onClick={onGoBack}
            >
              <FaChevronLeft />
              <span>Back</span>
            </button>

            <Link className="mx-auto" to="/">
              <LogoSvg className="w-20 h-auto" />
            </Link>
          </div>

          {renderContent()}
        </div>
      </div>

      <div
        className="hidden xl:flex items-center justify-center flex-1 xl:p-5 bg-center bg-no-repeat"
        style={{
          backgroundColor: "#f3ffff",
          backgroundImage: `url('/images/checkout/figure-bg.svg'`,
        }}
      >
        <div className="flex items-end justify-between gap-8 h-3/5">
          {Object.values(product.plans).map(({ id }) => (
            <Bar
              key={id}
              planId={id}
              readOnly={step > 0}
              active={plan?.id === id}
              onClick={() => setPlan(product.plans[id])}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
